import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Order, OrderItem, QueueSettings } from '../types';
import { getApiUrl, fetchWithCredentials } from '../lib/config';

// ==========================================
// Types
// ==========================================

interface QueueData {
  orders: Order[];
  settings: QueueSettings;
}

interface ApiResponse<T> {
  success?: boolean;
  order?: T;
  error?: string;
}

// ==========================================
// API Functions
// ==========================================

async function fetchQueue(shopId: string): Promise<QueueData> {
  const response = await fetch(getApiUrl(`/api/shops/${shopId}/queue`), {
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch queue');
  }

  const data = await response.json();
  return {
    orders: data.orders || [],
    settings: data.settings || {
      isActive: false,
      currentNumber: 0,
      estimatedWaitMinutes: 5,
    },
  };
}

async function callNextApi(shopId: string): Promise<ApiResponse<Order>> {
  const response = await fetch(getApiUrl(`/api/shops/${shopId}/queue/call-next`), {
    method: 'POST',
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to call next customer');
  }

  return response.json();
}

async function confirmOrderApi(orderId: string): Promise<ApiResponse<Order>> {
  const response = await fetch(getApiUrl(`/api/orders/${orderId}/confirm`), {
    method: 'PUT',
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to confirm order');
  }

  return response.json();
}

async function skipOrderApi(orderId: string): Promise<ApiResponse<Order>> {
  const response = await fetch(getApiUrl(`/api/orders/${orderId}/skip`), {
    method: 'POST',
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to skip order');
  }

  return response.json();
}

async function markReadyApi(orderId: string): Promise<ApiResponse<Order>> {
  const response = await fetch(getApiUrl(`/api/orders/${orderId}/ready`), {
    method: 'PUT',
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to mark order ready');
  }

  return response.json();
}

async function completeOrderApi(orderId: string): Promise<ApiResponse<Order>> {
  const response = await fetch(getApiUrl(`/api/orders/${orderId}/complete`), {
    method: 'PUT',
    ...fetchWithCredentials,
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to complete order');
  }

  return response.json();
}

async function updateOrderItemsApi(orderId: string, items: OrderItem[]): Promise<ApiResponse<Order>> {
  const response = await fetch(getApiUrl(`/api/orders/${orderId}/items`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    ...fetchWithCredentials,
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.error || 'Failed to update order');
  }

  return response.json();
}

// ==========================================
// Query Hook
// ==========================================

export function useQueueQuery(shopId: string) {
  return useQuery({
    queryKey: ['queue', shopId],
    queryFn: () => fetchQueue(shopId),
    staleTime: 10 * 1000,        // 10s - WebSocket invalidates sooner anyway
    refetchInterval: 30 * 1000,  // 30s polling fallback if WS fails
    refetchOnWindowFocus: true,  // Re-sync when user returns to tab
    refetchOnReconnect: true,    // Re-sync when network recovers
    enabled: !!shopId,
  });
}

// ==========================================
// Mutation Hooks with Optimistic Updates
// ==========================================

export function useCallNext(shopId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['queue', shopId];

  return useMutation({
    mutationFn: () => callNextApi(shopId),

    // OPTIMISTIC: Update UI immediately before API responds
    onMutate: async () => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey });

      // Snapshot current data for potential rollback
      const previousData = queryClient.getQueryData<QueueData>(queryKey);

      // Optimistically update: move first pending order to "called"
      if (previousData) {
        const pendingOrders = previousData.orders
          .filter(o => o.status === 'pending')
          .sort((a, b) => a.queueNumber - b.queueNumber);
        const nextOrder = pendingOrders[0];

        if (nextOrder) {
          queryClient.setQueryData<QueueData>(queryKey, {
            ...previousData,
            orders: previousData.orders.map(order =>
              order.id === nextOrder.id
                ? { ...order, status: 'called', calledAt: new Date().toISOString() }
                : order
            ),
          });
        }
      }

      return { previousData };
    },

    // ROLLBACK on error
    onError: (err, _vars, context) => {
      console.error('[useCallNext] Error:', err);
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    // RECONCILE: Always sync with server after mutation settles
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useConfirmOrder(shopId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['queue', shopId];

  return useMutation({
    mutationFn: (orderId: string) => confirmOrderApi(orderId),

    // OPTIMISTIC: Update status to confirmed immediately
    onMutate: async (orderId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<QueueData>(queryKey);

      if (previousData) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...previousData,
          orders: previousData.orders.map(order =>
            order.id === orderId
              ? { ...order, status: 'confirmed', confirmedAt: new Date().toISOString() }
              : order
          ),
        });
      }

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useSkipOrder(shopId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['queue', shopId];

  return useMutation({
    mutationFn: (orderId: string) => skipOrderApi(orderId),

    // OPTIMISTIC: Move order back to pending (skipped)
    onMutate: async (orderId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<QueueData>(queryKey);

      if (previousData) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...previousData,
          orders: previousData.orders.map(order =>
            order.id === orderId
              ? { ...order, status: 'pending', calledAt: undefined, skipCount: (order.skipCount || 0) + 1 }
              : order
          ),
        });
      }

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useMarkReady(shopId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['queue', shopId];

  return useMutation({
    mutationFn: (orderId: string) => markReadyApi(orderId),

    // OPTIMISTIC: Update status to ready immediately
    onMutate: async (orderId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<QueueData>(queryKey);

      if (previousData) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...previousData,
          orders: previousData.orders.map(order =>
            order.id === orderId
              ? { ...order, status: 'ready', readyAt: new Date().toISOString() }
              : order
          ),
        });
      }

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useCompleteOrder(shopId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['queue', shopId];

  return useMutation({
    mutationFn: (orderId: string) => completeOrderApi(orderId),

    // OPTIMISTIC: Remove order from queue immediately
    onMutate: async (orderId) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<QueueData>(queryKey);

      if (previousData) {
        queryClient.setQueryData<QueueData>(queryKey, {
          ...previousData,
          orders: previousData.orders.filter(order => order.id !== orderId),
        });
      }

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useUpdateOrderItems(shopId: string) {
  const queryClient = useQueryClient();
  const queryKey = ['queue', shopId];

  return useMutation({
    mutationFn: ({ orderId, items }: { orderId: string; items: OrderItem[] }) =>
      updateOrderItemsApi(orderId, items),

    // OPTIMISTIC: Update items immediately
    onMutate: async ({ orderId, items }) => {
      await queryClient.cancelQueries({ queryKey });
      const previousData = queryClient.getQueryData<QueueData>(queryKey);

      if (previousData) {
        // Calculate new total
        const totalAmount = items.reduce((sum, item) => {
          let itemPrice = item.unitPrice;
          if (item.size) itemPrice = item.size.price;
          if (item.variant) itemPrice += item.variant.priceModifier;
          if (item.milk) itemPrice += item.milk.priceModifier;
          return sum + (itemPrice * item.quantity);
        }, 0);

        queryClient.setQueryData<QueueData>(queryKey, {
          ...previousData,
          orders: previousData.orders.map(order =>
            order.id === orderId
              ? { ...order, items, totalAmount: String(totalAmount) }
              : order
          ),
        });
      }

      return { previousData };
    },

    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData);
      }
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
